import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { useRangeKey } from '../TimeRangeControl';
import { fetchDiscovery } from '../api';
import type { DiscoveryPayload } from '../types';
import { BuiltAt, QUERY_OPTIONS, TabStatus } from './_shared';

type Item = DiscoveryPayload['items'][number];

function DiscoveryRow({ item }: { item: Item }) {
  const sourceHref = `/sources/${encodeURIComponent(item.cite_key)}`;
  return (
    <li className="trends-discovery-row">
      <div className="trends-discovery-head">
        <span className={`trends-pill trends-pill-${item.entity_type.toLowerCase()}`}>
          {item.entity_type}
        </span>
        <span className="trends-discovery-value">{item.value}</span>
        {item.aliases.length > 0 && (
          <span className="trends-discovery-aliases">
            aliases: {item.aliases.slice(0, 3).join(', ')}
            {item.aliases.length > 3 && ` +${item.aliases.length - 3}`}
          </span>
        )}
        <span className="trends-discovery-when">
          {(item.first_seen || '').slice(0, 10)}
        </span>
      </div>
      {item.evidence && (
        <div className="trends-discovery-evidence">“{item.evidence}”</div>
      )}
      <div className="trends-discovery-source">
        <Link to={sourceHref} className="trends-entity-link">
          {item.cite_key}
        </Link>
      </div>
    </li>
  );
}

export default function DiscoveryTab() {
  const range = useRangeKey();
  const [activeType, setActiveType] = useState<string | null>(null);
  const q = useQuery({
    queryKey: ['trends', 'discovery', range],
    queryFn: () => fetchDiscovery(range),
    ...QUERY_OPTIONS,
  });

  const typeCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const it of q.data?.items ?? []) {
      counts[it.entity_type] = (counts[it.entity_type] ?? 0) + 1;
    }
    return counts;
  }, [q.data]);

  const filtered = useMemo(() => {
    if (!q.data) return [];
    if (!activeType) return q.data.items;
    return q.data.items.filter((it) => it.entity_type === activeType);
  }, [q.data, activeType]);

  return (
    <section className="trends-tab-content trends-discovery">
      <TabStatus
        isLoading={q.isLoading}
        isError={q.isError}
        error={q.error}
        data={q.data}
        emptyHint="No novel entities surfaced for this range. Discovery feeds from ioc_state.novel_entities (resolved_canonical_id IS NULL)."
      >
        {q.data && (
          <>
            <BuiltAt at={q.data.built_at} />
            <div className="trends-controls-row">
              <h2 className="trends-section-title">
                {filtered.length} novel entities
              </h2>
              <div className="trends-chip-filters" role="tablist" aria-label="Filter by type">
                <button
                  type="button"
                  className={`trends-chip${activeType === null ? ' active' : ''}`}
                  onClick={() => setActiveType(null)}
                >
                  All ({q.data.items.length})
                </button>
                {Object.entries(typeCounts)
                  .sort((a, b) => b[1] - a[1])
                  .map(([t, n]) => (
                    <button
                      key={t}
                      type="button"
                      className={`trends-chip${activeType === t ? ' active' : ''}`}
                      onClick={() => setActiveType(t)}
                    >
                      {t} ({n})
                    </button>
                  ))}
              </div>
            </div>
            <ul className="trends-discovery-list">
              {filtered.map((it) => (
                <DiscoveryRow key={`${it.cite_key}::${it.value}`} item={it} />
              ))}
            </ul>
          </>
        )}
      </TabStatus>
    </section>
  );
}
